import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { Integration } from "@cohesive/api-client";
import { icons, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";

import { Emoji, MetadataIcon } from "@/types";
import emojiData from "../../data/emoji-data.json";
import { IntegrationIcon } from "../IntegrationIcon";

export interface IconSelectorProps {
  onIconChange?: (icon?: MetadataIcon | null) => void;
  selectedIcon?: MetadataIcon | null;
  integrations?: Integration[];
}

type IconName = string;

// returns an emoji from a unified hex string
function parseNativeEmoji(unified: string = ""): string {
  if (!unified) return "";
  return unified
    .split("-")
    .map((hex) => String.fromCodePoint(parseInt(hex, 16)))
    .join("");
}

// returns a unified hex string of an emoji
function toUnifiedHexString(emoji: string): string {
  if (!emoji) return "";
  return Array.from(emoji)
    .map((char) => char.codePointAt(0)?.toString(16) ?? "") // Convert each char to hex, handle potential undefined
    .join("-"); // Join the hex codes with a dash
}

function createEmojiWithSkinTone(
  emoji: string,
  selectedSkinTone: string,
): string {
  if (selectedSkinTone === "" || selectedSkinTone === "fe0f") {
    return emoji;
  }
  const unifiedEmoji = toUnifiedHexString(emoji);
  const splitEmoji = unifiedEmoji.split("-") as string[];

  if (selectedSkinTone) {
    let split = unifiedEmoji.split("-");

    if (splitEmoji[1] === "fe0f") {
      split[1] = selectedSkinTone;
    } else {
      split.splice(1, 0, selectedSkinTone);
    }

    const modifiedEmoji = parseNativeEmoji(split.join("-"));

    return modifiedEmoji;
  }

  return emoji;
}

const skinTones = ["", "1f3fb", "1f3fc", "1f3fd", "1f3fe", "1f3ff"];

export const colors = [
  "#55534E", // Default
  "#A6A299", // Light Gray
  "#9F6B53", // Brown
  "#CB912F", // Yellow
  "#D9730D", // Orange
  "#448361", // Green
  "#337EA9", // Blue
  "#9065B0", // Purple
  "#C14C8A", // Pink
  "#D44C47", // Red
];

export const colorBackgrounds = {
  light: {
    "#55534E": "#E8E7E5", // Default
    "#A6A299": "#F2F1EF", // Light Gray
    "#9F6B53": "#F1E2DB", // Brown
    "#CB912F": "#F9EDD7", // Yellow
    "#D9730D": "#FAE6D5", // Orange
    "#448361": "#DBE9E0", // Green
    "#337EA9": "#D9E8F2", // Blue
    "#9065B0": "#EBE2F3", // Purple
    "#C14C8A": "#F6DEE9", // Pink
    "#D44C47": "#F9DDDC", // Red
  },
  dark: {
    "#55534E": "#2E2D2A", // Default
    "#A6A299": "#4A4845", // Light Gray
    "#9F6B53": "#4D3529", // Brown
    "#CB912F": "#5F4516", // Yellow
    "#D9730D": "#663606", // Orange
    "#448361": "#1F3C2C", // Green
    "#337EA9": "#183B4F", // Blue
    "#9065B0": "#432F53", // Purple
    "#C14C8A": "#5A2340", // Pink
    "#D44C47": "#642321", // Red
  },
};

export const getIconBackgroundColorClass = (
  icon?: MetadataIcon | null | undefined,
) => {
  if (icon?.type === "emoji") {
    return;
  }

  if (icon?.type !== "lucide") {
    return;
  }

  const color = icon.lucide?.color || (colors[0] as string);
  const lightBgColor =
    colorBackgrounds.light[color as keyof typeof colorBackgrounds.light];
  const darkBgColor =
    colorBackgrounds.dark[color as keyof typeof colorBackgrounds.dark];
  const bgColorClass = `bg-[${lightBgColor}] dark:bg-[${darkBgColor}]`;

  return bgColorClass;
};

// Add this constant for category ordering
const EMOJI_CATEGORY_ORDER = [
  "Smileys & Emotion",
  "People & Body",
  "Animals & Nature",
  "Food & Drink",
  "Activities",
  "Travel & Places",
  "Objects",
  "Symbols",
  "Flags",
];

export const IconSelector = ({
  onIconChange,
  selectedIcon,
  integrations = [],
}: IconSelectorProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const allIntegrationConnectionsResponse = [
    {
      id: "cohesive",
      name: "Cohesive",
      platform: "cohesive",
      status: "",
      provider: "",
      provider_id: "",
      auth_level: "user",
      auth_method: "user",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      metadata: {
        image_url: `${import.meta.env.VITE_STATIC_BASE_URL || ""}/images/integrations/logos/cohesive.svg`,
      },
    },
    ...integrations,
  ];

  const [activeTab, setActiveTab] = useState<
    "emojis" | "icons" | "integrations"
  >(
    selectedIcon?.type === "lucide"
      ? "icons"
      : selectedIcon?.type === "custom"
        ? "integrations"
        : "emojis",
  );

  const [filter, setFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState(filter);
  const [selectedColor, setSelectedColor] = useState(
    selectedIcon?.type === "lucide" ? selectedIcon.lucide?.color : colors[0],
  );
  const [selectedIconName, setSelectedIconName] = useState<IconName | null>(
    (selectedIcon?.type === "lucide"
      ? selectedIcon.lucide?.name
      : null) as IconName,
  );

  const [selectedSkinTone, setSelectedSkinTone] = useState("");
  const [recentEmojis, setRecentEmojis] = useState<string[]>([]);
  /** Inline color / skin-tone picker visibility. We deliberately
   *  avoid nesting another `<Popover>` here — a popover-inside-a-
   *  popover-inside-a-Dialog runs into Radix DismissableLayer and
   *  focus-trap issues that the parent dialog inherits, and clicks
   *  on the inner picker get swallowed before they can register.
   *  An inline expanding row under the search field sidesteps the
   *  whole portal/stacking problem and stays right next to the
   *  trigger that opened it. */
  const [pickerOpen, setPickerOpen] = useState(false);

  // Close the inline picker whenever the user switches tabs — the
  // color picker belongs to the Icons tab and the skin-tone picker
  // to the Emojis tab, so leaving the panel open across tabs would
  // surface controls that don't apply to the current view.
  useEffect(() => {
    setPickerOpen(false);
  }, [activeTab]);

  useEffect(() => {
    const storedSkinTone = localStorage.getItem("selectedSkinTone");
    if (storedSkinTone) setSelectedSkinTone(storedSkinTone);

    const storedRecentEmojis = localStorage.getItem("recentEmojis");
    if (storedRecentEmojis) setRecentEmojis(JSON.parse(storedRecentEmojis));
  }, []);

  useEffect(() => {
    localStorage.setItem("selectedSkinTone", selectedSkinTone);
  }, [selectedSkinTone]);

  useEffect(() => {
    localStorage.setItem("recentEmojis", JSON.stringify(recentEmojis));
  }, [recentEmojis]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilter(filter), 200);
    return () => clearTimeout(timer);
  }, [filter]);

  const iconList = useMemo(() => {
    return Object.entries(icons)
      .filter(([name]) =>
        name.toLowerCase().includes(debouncedFilter.toLowerCase()),
      )
      .map(([name, Icon]) => (
        <Button
          key={name}
          variant="ghost"
          className="p-2"
          onClick={() => handleIconSelect(name as IconName)}
        >
          <Icon
            className={`h-6 w-6 text-[${selectedColor}]`}
            color={selectedColor}
          />
        </Button>
      ));
  }, [debouncedFilter, selectedColor]);

  // Update the emojiList to respect the category order
  const emojiList = useMemo(() => {
    if (!emojiData) return null;

    return EMOJI_CATEGORY_ORDER.map((categoryName) => {
      const group = emojiData[categoryName as keyof typeof emojiData];
      if (!group) return null;

      const filteredEmojis = group.emojis
        .filter((emoji: Emoji) => {
          const searchTerm = debouncedFilter.toLowerCase();
          return (
            emoji.name.toLowerCase().includes(searchTerm) ||
            emoji.keywords.some((keyword: string) =>
              keyword.toLowerCase().includes(searchTerm),
            )
          );
        })
        .filter((emoji: Emoji) => {
          return Number(emoji.unicode_version) < 15;
        });

      if (filteredEmojis.length === 0) return null;

      return (
        <div key={group.slug} className="mb-4">
          <h3 className="text-sm font-medium mb-2">{group.name}</h3>
          <div className="grid grid-cols-8 gap-1">
            {filteredEmojis.map((baseEmoji: Emoji) => {
              const displayEmoji =
                baseEmoji.skin_tone_support && selectedSkinTone
                  ? createEmojiWithSkinTone(baseEmoji.emoji, selectedSkinTone)
                  : baseEmoji.emoji;

              return (
                <Button
                  key={baseEmoji.slug}
                  variant="ghost"
                  className="p-2 text-[30px]"
                  onClick={() => handleEmojiSelect(displayEmoji)}
                >
                  {displayEmoji}
                </Button>
              );
            })}
          </div>
        </div>
      );
    }).filter(Boolean);
  }, [emojiData, debouncedFilter, selectedSkinTone]);

  const handleIconSelect = (iconName: IconName) => {
    setSelectedIconName(iconName);
    onIconChange?.({
      type: "lucide",
      lucide: {
        name: iconName,
        color: selectedColor || (colors[0] as string),
      },
    });
  };

  const handleEmojiSelect = (emoji: string) => {
    setSelectedIconName(null);
    onIconChange?.({
      type: "emoji",
      emoji: emoji,
    });
    updateRecentEmojis(emoji);
  };

  const handleColorSelect = (color: string) => {
    setSelectedColor(color);
    if (selectedIconName) {
      onIconChange?.({
        type: "lucide",
        lucide: {
          name: selectedIconName,
          color: color,
        },
      });
    }
  };

  const handleSkinToneSelect = (skinTone: string) => {
    setSelectedSkinTone(skinTone);
  };

  const handleRemove = () => {
    setSelectedIconName(null);
    onIconChange?.(null);
  };

  const updateRecentEmojis = (emoji: string) => {
    setRecentEmojis((prev) => {
      const newRecent = [emoji, ...prev.filter((e) => e !== emoji)].slice(0, 8);
      return newRecent;
    });
  };

  useEffect(() => {
    const handleScroll = (e: Event) => {
      e.stopPropagation();
    };

    window.addEventListener("wheel", handleScroll, true);
    window.addEventListener("touchmove", handleScroll, true);
    window.addEventListener("touchstart", handleScroll, true);

    return () => {
      window.removeEventListener("wheel", handleScroll, true);
      window.removeEventListener("touchmove", handleScroll, true);
      window.removeEventListener("touchstart", handleScroll, true);
    };
  }, []);

  const handleTabClick = () => {
    inputRef.current?.focus();
  };

  return (
    <div>
      <Tabs
        defaultValue={activeTab}
        onValueChange={(value) =>
          setActiveTab(value as "emojis" | "icons" | "integrations")
        }
      >
        <div className="flex justify-between items-center px-4 py-2 border-b">
          <TabsList>
            <TabsTrigger onClick={handleTabClick} value="emojis">
              Emojis
            </TabsTrigger>
            <TabsTrigger onClick={handleTabClick} value="icons">
              Icons
            </TabsTrigger>
            {allIntegrationConnectionsResponse.length > 0 && (
              <TabsTrigger onClick={handleTabClick} value="integrations">
                Integrations
              </TabsTrigger>
            )}
          </TabsList>
          <Button variant="ghost" size="sm" onClick={handleRemove}>
            Remove
          </Button>
        </div>
        <div className="p-4">
          <div className="mb-4 flex items-center space-x-2">
            <div className="relative grow">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-neutral-400 size-4" />
              <Input
                ref={inputRef}
                type="text"
                placeholder="Filter..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="pl-7 pr-8"
                autoFocus
              />
              {filter && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 size-6"
                  onClick={() => setFilter("")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            {activeTab === "icons" ? (
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "size-8",
                  pickerOpen && "bg-accent text-accent-foreground",
                )}
                aria-label="Choose icon color"
                aria-expanded={pickerOpen}
                onClick={() => setPickerOpen((v) => !v)}
              >
                <div
                  className="w-5 h-5 rounded-full"
                  style={{ backgroundColor: selectedColor }}
                />
              </Button>
            ) : activeTab === "emojis" ? (
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "size-8 text-[30px]",
                  pickerOpen && "bg-accent text-accent-foreground",
                )}
                aria-label="Choose emoji skin tone"
                aria-expanded={pickerOpen}
                onClick={() => setPickerOpen((v) => !v)}
              >
                {selectedSkinTone
                  ? `👋${parseNativeEmoji(selectedSkinTone)}`
                  : "👋"}
              </Button>
            ) : null}
          </div>
          {/** Inline picker row — rendered just under the search +
           *   trigger row so it visually anchors to the swatch that
           *   opened it. Toggled by `pickerOpen`; rendering nothing
           *   when collapsed keeps focus order clean and avoids
           *   reserving vertical space when the picker is unused. */}
          {pickerOpen && activeTab === "icons" && (
            <div className="mb-4 flex flex-wrap items-center gap-1 rounded-md border bg-muted/30 p-2">
              {colors.map((color) => {
                const isActive = color === selectedColor;
                return (
                  <button
                    key={color}
                    type="button"
                    aria-label={`Color ${color}`}
                    aria-pressed={isActive}
                    onClick={() => handleColorSelect(color)}
                    className={cn(
                      "inline-flex h-7 w-7 items-center justify-center rounded-full transition-transform cursor-pointer",
                      "hover:scale-110 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
                      isActive && "ring-2 ring-ring ring-offset-1",
                    )}
                  >
                    <span
                      className="block h-5 w-5 rounded-full ring-1 ring-black/10"
                      style={{ backgroundColor: color }}
                    />
                  </button>
                );
              })}
            </div>
          )}
          {pickerOpen && activeTab === "emojis" && (
            <div className="mb-4 flex flex-wrap items-center gap-1 rounded-md border bg-muted/30 p-2">
              {skinTones.map((tone) => {
                const isActive = tone === selectedSkinTone;
                return (
                  <button
                    key={tone || "default"}
                    type="button"
                    aria-label={
                      tone ? `Skin tone ${tone}` : "Default skin tone"
                    }
                    aria-pressed={isActive}
                    onClick={() => handleSkinToneSelect(tone)}
                    className={cn(
                      "inline-flex h-8 w-8 items-center justify-center rounded-full text-2xl leading-none transition-transform cursor-pointer",
                      "hover:scale-110 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
                      isActive && "ring-2 ring-ring ring-offset-1",
                    )}
                  >
                    {tone ? `👋${parseNativeEmoji(tone)}` : "👋"}
                  </button>
                );
              })}
            </div>
          )}
          <ScrollArea className="h-64">
            <TabsContent value="icons">
              <div className="grid grid-cols-8 gap-1">{iconList}</div>
            </TabsContent>
            <TabsContent value="emojis">
              {recentEmojis.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium mb-2">Recent</h3>
                  <div className="grid grid-cols-8 gap-1">
                    {recentEmojis.map((emoji) => (
                      <Button
                        key={emoji}
                        variant="ghost"
                        className="p-2 text-[30px]"
                        onClick={() => handleEmojiSelect(emoji)}
                      >
                        {emoji}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              {emojiList}
            </TabsContent>
            {allIntegrationConnectionsResponse?.length > 0 && (
              <TabsContent value="integrations">
                <div className="grid grid-cols-8 gap-1">
                  {allIntegrationConnectionsResponse
                    ?.filter(
                      (integration) =>
                        integration.name
                          ?.toLowerCase()
                          .includes(debouncedFilter.toLowerCase()) ||
                        integration.platform
                          ?.toLowerCase()
                          .includes(debouncedFilter.toLowerCase()),
                    )
                    .map((integration) => (
                      <Button
                        key={integration.provider_id}
                        variant="ghost"
                        className="p-1"
                        onClick={() =>
                          onIconChange?.({
                            type: "custom",
                            custom: {
                              url: `${import.meta.env.VITE_STATIC_BASE_URL || ""}/images/integrations/logos/${integration.platform}.svg`,
                            },
                          })
                        }
                      >
                        <IntegrationIcon
                          platform={integration.platform}
                          name={integration.name}
                          imageURL={integration.metadata?.image_url}
                          className="h-5 w-5"
                        />
                      </Button>
                    ))}
                </div>
              </TabsContent>
            )}
          </ScrollArea>
        </div>
      </Tabs>
    </div>
  );
};

export default IconSelector;
